import { Group } from "@fairfleet/geotab";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import PublishIcon from "@mui/icons-material/Publish";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import { ManageColumnsDialog } from "@/components/dialogs/ManageColumnsDialog";
import { ViewMetadataDialog } from "@/components/dialogs/ViewMetadataDialog";
import { useViewContext } from "@/components/ViewProvider";
import { useDeleteView } from "@/hooks/views/useDeleteView";
import { useResetViewOverride } from "@/hooks/views/useResetViewOverride";
import { useSaveView } from "@/hooks/views/useSaveView";
import { DiagnosticView, ViewColumn, ViewPageType, ViewSettings } from "@/models/DiagnosticView";

export function AdminViewActions() {
  const { t } = useTranslation();
  const { isFairfleetAdmin, selectedView, pageType, setSelectedView } = useViewContext();
  const dataGrid = useDataGridSettings();
  const { apiRef } = dataGrid;
  const { saveView } = useSaveView();
  const { deleteView } = useDeleteView();
  const { resetOverride } = useResetViewOverride(pageType);

  const captureSettings = (): ViewSettings => ({
    precisions: dataGrid.precisions,
    colorIntervals: dataGrid.colorIntervals,
    columnNames: dataGrid.columnNames,
    hideOldData: dataGrid.hideOldData,
    includeDismissedWarnings: dataGrid.includeDismissedWarnings,
    deviceCountLimit: dataGrid.deviceCountLimit,
    loadAddresses: dataGrid.loadAddresses,
    chargeRateOnlyWhenCharging: dataGrid.chargeRateOnlyWhenCharging,
    showArchivedVehicles: dataGrid.showArchivedVehicles,
    alertRules: dataGrid.alertRules,
    alertDisplayMode: dataGrid.alertDisplayMode,
    alertAutoDismiss: dataGrid.alertAutoDismiss,
  });

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (!isFairfleetAdmin) return null;

  const openMenu = (e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const captureGridState = (): string => {
    const state = apiRef.current?.exportState();
    if (state?.pagination) {
      state.pagination.rowCount = undefined;
      state.pagination.paginationModel = undefined;
    }
    return JSON.stringify(state ?? {});
  };

  const handleCreate = async (meta: { name: string; description?: string; groups: Group[] }) => {
    const newView: DiagnosticView = {
      id: crypto.randomUUID(),
      name: meta.name,
      description: meta.description,
      pageTypes: [pageType as ViewPageType],
      groups: meta.groups,
      columns: selectedView?.columns ?? [],
      gridState: captureGridState(),
      settings: captureSettings(),
      updatedAt: Date.now(),
      updatedBy: "",
    };
    await saveView(newView);
    setSelectedView(newView);
    setCreateOpen(false);
  };

  const handlePublish = async () => {
    if (!selectedView) return;
    await saveView({
      ...selectedView,
      gridState: captureGridState(),
      settings: captureSettings(),
    });
    await resetOverride(selectedView.id);
    closeMenu();
  };

  const handleEditMetadata = async (meta: { name: string; description?: string; groups: Group[] }) => {
    if (!selectedView) return;
    await saveView({ ...selectedView, ...meta });
    setEditOpen(false);
  };

  const handleManageColumns = async (columns: ViewColumn[]) => {
    if (!selectedView) return;
    await saveView({ ...selectedView, columns });
    setColumnsOpen(false);
  };

  const handleDelete = async () => {
    if (!selectedView) return;
    await deleteView(selectedView.id);
    setSelectedView(null);
    setDeleteOpen(false);
  };

  return (
    <>
      <Tooltip title={t("views.menu.button")}>
        <IconButton size="small" onClick={openMenu}>
          <MoreVertIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {selectedView && [
          <MenuItem key="cols" onClick={() => { closeMenu(); setColumnsOpen(true); }}>
            <ListItemIcon><ViewColumnIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t("views.menu.manageColumns")}</ListItemText>
          </MenuItem>,
          <MenuItem key="edit" onClick={() => { closeMenu(); setEditOpen(true); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t("views.menu.editDetails")}</ListItemText>
          </MenuItem>,
          <MenuItem key="pub" onClick={handlePublish}>
            <ListItemIcon><PublishIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t("views.menu.publishLayout")}</ListItemText>
          </MenuItem>,
          <Divider key="div" />,
        ]}
        <MenuItem onClick={() => { closeMenu(); setCreateOpen(true); }}>
          <ListItemIcon><PlaylistAddIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t("views.menu.saveAsNew")}</ListItemText>
        </MenuItem>
        {selectedView && (
          <MenuItem onClick={() => { closeMenu(); setDeleteOpen(true); }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>{t("views.menu.deleteView")}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <ViewMetadataDialog
        open={createOpen}
        title={t("views.dialog.titleCreate")}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />
      <ViewMetadataDialog
        open={editOpen}
        initial={selectedView ?? undefined}
        title={t("views.dialog.titleEdit")}
        onClose={() => setEditOpen(false)}
        onSubmit={handleEditMetadata}
      />
      {selectedView && (
        <ManageColumnsDialog
          open={columnsOpen}
          view={selectedView}
          onClose={() => setColumnsOpen(false)}
          onSubmit={handleManageColumns}
        />
      )}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t("views.deleteConfirm.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("views.deleteConfirm.message", { name: selectedView?.name ?? "" })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t("common.cancel")}</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
